import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { buildMonthlyActualCostData } from '../lib/monthlyActualCost'

const fmt = (n) => n ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'
const fmtM = (n) => {
  if (!n) return '0'
  if (Math.abs(n) >= 1000000) return (n/1000000).toFixed(2) + 'M'
  if (Math.abs(n) >= 1000) return (n/1000).toFixed(1) + 'K'
  return fmt(n)
}

const periodToNum = (p) => p ? parseInt(p.replace('-','')) : 0
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December']
const monthLabel = (p) => { // '2025-03' -> 'March 2025'
  if (!p) return '—'
  const [y,m] = p.split('-')
  const idx = parseInt(m,10)-1
  return `${MONTH_NAMES[idx]||m} ${y}`
}

// Source display order
const SOURCE_ORDER = ['IPC','Payroll','Store','Petty Cash','Accrual']
const SOURCE_COLORS = {
  'IPC':        { bg: '#EFF6FF', text: '#1D4ED8' },
  'Payroll':    { bg: '#FEF3C7', text: '#92400E' },
  'Store':      { bg: '#F0FDF4', text: '#14532D' },
  'Petty Cash': { bg: '#FDF4FF', text: '#6B21A8' },
  'Accrual':    { bg: '#FFF7ED', text: '#9A3412' },
}

// Presentation-only fix: IPC and Petty Cash record transactions against a
// Sub Code, not a real Cost Code, so monthlyActualCost.js puts a fixed
// placeholder ('IPC' / 'Petty Cash') in costCode for those two sources.
// That placeholder is not a real code and has no description of its own —
// it must never display the description of whichever Sub Code happened to
// be grouped first. This does not touch costCode/subCode/source/periods,
// only how the group-header row's description cell is derived below.
const isPlaceholderCostCode = (costCode, source) => costCode === source

export default function MonthlyActualCost({ initProject }) {
  const [projects, setProjects]           = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [loading, setLoading]             = useState(false)
  const [rows, setRows]                   = useState([])
  const [periods, setPeriods]             = useState([])
  const [search, setSearch]               = useState('')
  const [expandedCodes, setExpandedCodes] = useState(new Set())
  const [showAll, setShowAll]             = useState(false)
  const [selectedPeriods, setSelectedPeriods] = useState(new Set()) // date filter
  const [printMode, setPrintMode] = useState(null) // null | 'current' | 'all'
  const [currentGroupBy, setCurrentGroupBy] = useState('sub') // 'sub' | 'cost'
  const [selectedMonth, setSelectedMonth] = useState(null) // null = latest
  const [allGroupBy, setAllGroupBy] = useState('sub') // 'sub' | 'cost'
  const [totalGroupBy, setTotalGroupBy] = useState('sub') // 'sub' | 'cost'

  const [viewMode, setViewMode]           = useState('source') // 'source' | 'hierarchy'
  // hierarchyGroupBy selects WHICH grouping projection the hierarchy
  // table/reports display: 'resourceType' (report.groupings.resourceType)
  // or 'resourceCode' (report.groupings.resourceCode). Both are peers —
  // same shape, same rendering, same expand/collapse behavior, same
  // totals/movement — produced by the exact same buildHierarchyFromRows()
  // call in the data layer, differing only in which classifier was
  // passed in. No component below branches on this value's identity to
  // run different logic; it only selects which pre-built tree + label to
  // pass to the shared rendering path.
  const [hierarchyGroupBy, setHierarchyGroupBy] = useState('resourceType') // 'resourceType' | 'resourceCode'
  const [expandedTypes, setExpandedTypes] = useState(new Set())
  const [expandedCC, setExpandedCC]       = useState(new Set())
  const [summary, setSummary]             = useState(null)
  // Read from report.groupings.resourceType / .resourceCode and
  // report.groupingsByPeriod.resourceType / .resourceCode (the target
  // contract) — NOT the deprecated report.hierarchy /
  // report.hierarchyByPeriod aliases. groupings holds BOTH projections
  // as peers; the UI selects which one to render via hierarchyGroupBy
  // above, never by asking the data layer to build a different shape.
  const [groupings, setGroupings] = useState({
    resourceType: { Direct: {}, Indirect: {} },
    resourceCode: { Direct: {}, Indirect: {} },
  })
  const [groupingsByPeriod, setGroupingsByPeriod] = useState({ resourceType: {}, resourceCode: {} })
  // grandTotals/grandTotal/cumulativeTotals/grandMovement are read
  // directly from buildMonthlyActualCostData()'s result — the data layer
  // already computes these; the presentation layer must never recompute
  // them (previously this file re-derived them locally via rows.reduce()
  // on every render, which is now removed).
  const [grandTotals, setGrandTotals]     = useState({})
  const [grandTotal, setGrandTotal]       = useState(0)
  const [cumulativeTotals, setCumulativeTotals] = useState({})
  const [grandMovement, setGrandMovement] = useState(0)

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({data}) => setProjects(data||[]))
  }, [])

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject])
  useEffect(() => { if (selectedProject?.id) loadData(selectedProject.id) }, [selectedProject?.id]) // eslint-disable-line

  const loadData = useCallback(async (projectId) => {
    setLoading(true)
    setRows([])
    setPeriods([])
    setSelectedPeriods(new Set())
    setSummary(null)
    setGroupings({ resourceType: { Direct: {}, Indirect: {} }, resourceCode: { Direct: {}, Indirect: {} } })
    setGroupingsByPeriod({ resourceType: {}, resourceCode: {} })
    setGrandTotals({})
    setGrandTotal(0)
    setCumulativeTotals({})
    setGrandMovement(0)

    // All aggregation logic (IPC/Payroll/Store/Petty Cash/Accrual sources,
    // period bucketing, grand/cumulative totals, Movement) now lives in one
    // place: src/lib/monthlyActualCost.js (buildMonthlyActualCostData). This
    // page no longer duplicates any of that — it only requests the result
    // and renders it, exactly as before. `groupings`, `groupingsByPeriod`,
    // and `summary` are additive presentation-only fields — read directly,
    // never recomputed here. groupingsByPeriod is produced in the SAME
    // data load as everything else — reports never trigger a second query
    // to get a month-specific view (One load. One source of truth.
    // Multiple projections.). grandTotals/grandTotal/cumulativeTotals/
    // grandMovement are likewise read as-is — no financial aggregation
    // happens in this component anywhere. groupings/groupingsByPeriod are
    // stored WHOLE (both resourceType and resourceCode together) — the
    // target contract — never the deprecated report.hierarchy /
    // report.hierarchyByPeriod aliases.
    const result = await buildMonthlyActualCostData(projectId)

    setPeriods(result.periods)
    setRows(result.rows)
    setSummary(result.summary)
    setGroupings(result.groupings)
    setGroupingsByPeriod(result.groupingsByPeriod)
    setGrandTotals(result.grandTotals)
    setGrandTotal(result.grandTotal)
    setCumulativeTotals(result.cumulativeTotals)
    setGrandMovement(result.grandMovement)
    setLoading(false)
  }, [])

  // Active periods (filtered) — a presentation filter only: it controls
  // which period columns are visible, never what the totals mean. Grand
  // Total / Grand Movement below are always read as-is from the data
  // layer's result (buildMonthlyActualCostData), never recomputed here —
  // if the data layer already exposes a value, the presentation layer
  // must never recompute it.
  const activePeriods = selectedPeriods.size > 0 ? periods.filter(p => selectedPeriods.has(p)) : periods

  // Group by source → costCode → subCode
  // Cost Code group headers use r.resolvedCostCodeDesc (ADDITIVE field
  // from monthlyActualCost.js — this row's resolved Level-1 Cost Code's
  // own description, via the same realCodeFor() resolution used by
  // byCostCode/hierarchy) rather than r.costCodeDesc, which means "the
  // description of whichever code this row was posted against" (usually
  // the Sub Code) — correct for the Sub Code row below, but wrong for the
  // parent Cost Code group header, which would otherwise borrow whichever
  // Sub Code's row happened to be grouped first.
  const grouped = {}
  rows.forEach(r => {
    const srcKey = r.source
    if (!grouped[srcKey]) grouped[srcKey] = {}
    const ccKey = r.costCode
    // Placeholder costCodes (IPC / Petty Cash) have no real description of
    // their own — show blank. Real costCodes read resolvedCostCodeDesc.
    if (!grouped[srcKey][ccKey]) {
      grouped[srcKey][ccKey] = { desc: isPlaceholderCostCode(ccKey, srcKey) ? '' : r.resolvedCostCodeDesc, subs: [] }
    }
    grouped[srcKey][ccKey].subs.push(r)
  })

  // Filter by search
  const q = search.toLowerCase()
  const filteredSources = Object.entries(grouped).map(([src, codes]) => {
    const filteredCodes = Object.entries(codes).filter(([cc, {subs}]) =>
      !q || cc.toLowerCase().includes(q) || subs.some(s => s.subCode.toLowerCase().includes(q)) ||
      (codes[cc].desc||'').toLowerCase().includes(q)
    )
    return [src, Object.fromEntries(filteredCodes)]
  }).filter(([,codes]) => Object.keys(codes).length > 0)

  const expandKey = (src, cc) => `${src}||${cc}`
  const toggleExpand = (src, cc) => {
    const k = expandKey(src, cc)
    setExpandedCodes(prev => { const n = new Set(prev); n.has(k)?n.delete(k):n.add(k); return n })
  }
  const togglePeriod = (p) => setSelectedPeriods(prev => { const n = new Set(prev); n.has(p)?n.delete(p):n.add(p); return n })

  // Resource Type hierarchy expand/collapse — independent state from the
  // By Source view above, so switching views doesn't reset either one.
  // sectionPrefix ('direct'/'indirect') keeps Direct and Indirect keys
  // distinct even if a Resource Type name and a Category name happened to
  // collide (e.g. both named "Other") — expand/collapse state for one
  // section never leaks into the other.
  const toggleType = (sectionPrefix, rt) => {
    const k = `${sectionPrefix}||${rt}`
    setExpandedTypes(prev => { const n = new Set(prev); n.has(k)?n.delete(k):n.add(k); return n })
  }
  const ccKey2 = (sectionPrefix, rt, cc) => `${sectionPrefix}||${rt}||${cc}`
  const toggleCC2 = (sectionPrefix, rt, cc) => {
    const k = ccKey2(sectionPrefix, rt, cc)
    setExpandedCC(prev => { const n = new Set(prev); n.has(k)?n.delete(k):n.add(k); return n })
  }

  // Filter the hierarchy tree by the same search box, applied at
  // grouping-key / Cost Code / Sub Code text — presentation-only
  // filtering, same pattern as filteredSources above, no recalculation of
  // any amount. Reads groupings[hierarchyGroupBy] — a plain lookup by the
  // selected mode's key, not a branch on business logic: 'resourceType'
  // and 'resourceCode' are peers here, selected the same way.
  //
  // Direct and Indirect are rendered as two sections within the SAME
  // grouping mode (no separate tab) — each with its own business
  // hierarchy: Direct groups by Resource Type/Resource Code -> Cost Code
  // -> Sub Code; Indirect groups by Category -> Cost Code -> Sub Code
  // (no Resource Type/Code level, reusing Cost Control's own
  // categorization verbatim). filterSection() is the same filtering
  // logic applied twice — once per section — never a different
  // implementation for Indirect.
  const activeGrouping = groupings[hierarchyGroupBy] || { Direct: {}, Indirect: {} }
  const filterSection = (sectionTree) => Object.entries(sectionTree || {}).map(([rt, rtNode]) => {
    const filteredCC = Object.entries(rtNode.costCodes || {}).filter(([cc, ccNode]) =>
      !q || rt.toLowerCase().includes(q) || cc.toLowerCase().includes(q) ||
      (ccNode.desc||'').toLowerCase().includes(q) ||
      Object.entries(ccNode.subCodes||{}).some(([sc, scNode]) =>
        sc.toLowerCase().includes(q) ||
        (scNode.desc||'').toLowerCase().includes(q) ||
        (scNode.source||'').toLowerCase().includes(q)
      )
    )
    return [rt, rtNode, Object.fromEntries(filteredCC)]
  }).filter(([,,costCodes]) => Object.keys(costCodes).length > 0)

  const filteredDirectHierarchy = filterSection(activeGrouping.Direct)
  const filteredIndirectHierarchy = filterSection(activeGrouping.Indirect)

  // toggleAll expands/collapses whichever tab is currently active — By
  // Source uses expandedCodes (Cost Code level only); the hierarchy view
  // (By Resource Type / By Resource Code) uses two independent expand
  // sets, expandedTypes (Resource Type/Category level) and expandedCC
  // (Cost Code level), across BOTH the Direct and Indirect sections. This
  // must stay in sync with whichever grouping mode is on screen — if a
  // new tab is ever added, its expand state belongs in this same
  // function, not a separate button.
  const toggleAll = () => {
    if (showAll) {
      setExpandedCodes(new Set())
      setExpandedTypes(new Set())
      setExpandedCC(new Set())
      setShowAll(false)
    } else {
      if (viewMode === 'source') {
        const all = new Set()
        filteredSources.forEach(([src, codes]) => Object.keys(codes).forEach(cc => all.add(expandKey(src,cc))))
        setExpandedCodes(all)
      } else {
        const allTypes = new Set()
        const allCC = new Set()
        const collectSection = (entries, sectionPrefix) => entries.forEach(([rt, , costCodes]) => {
          allTypes.add(`${sectionPrefix}||${rt}`)
          Object.keys(costCodes).forEach(cc => allCC.add(ccKey2(sectionPrefix, rt, cc)))
        })
        collectSection(filteredDirectHierarchy, 'direct')
        collectSection(filteredIndirectHierarchy, 'indirect')
        setExpandedTypes(allTypes)
        setExpandedCC(allCC)
      }
      setShowAll(true)
    }
  }

  // Renders one section's (Direct's or Indirect's) rows — the exact same
  // rendering logic for both, called twice with a different `entries`
  // array and `sectionPrefix`. No branch anywhere in here checks whether
  // it's rendering Direct or Indirect; it only reads whatever tree it was
  // given. This is what keeps Indirect from becoming a parallel
  // implementation — it's the same render path, different input.
  const renderGroupEntries = (entries, sectionPrefix) => entries.map(([rt, rtNode, costCodes]) => {
    const typeKey = `${sectionPrefix}||${rt}`
    const isRTExpanded = expandedTypes.has(typeKey)
    return [
      // Group header row (Resource Type / Resource Code / Category,
      // depending on hierarchyGroupBy and section) — reads rtNode.total/
      // periodTotals/movement directly, all produced by
      // buildHierarchyFromRows() as a Σ of rows already in the financial
      // engine's output.
      <tr key={`rt-${typeKey}`} onClick={()=>toggleType(sectionPrefix, rt)} style={{cursor:'pointer',background:'#F0F4FF',borderTop:'2px solid #E2DCD1'}}>
        <td colSpan={3} style={{position:'sticky',left:0,zIndex:20,background:'#F0F4FF',padding:'6px 12px',fontWeight:700,fontSize:12,color:'#0B3D91',borderRight:'2px solid #DDD9D0'}}>
          <span style={{marginRight:6,color:'#9A948C',fontSize:10}}>{isRTExpanded?'▼':'▶'}</span>
          {rt}
          <span style={{marginLeft:6,color:'#9A948C',fontSize:10}}>({Object.keys(costCodes).length})</span>
        </td>
        {activePeriods.map(p=>(
          <td key={p} style={{textAlign:'right',padding:'6px 10px',fontWeight:700,color:'#0B3D91',fontSize:12}}>
            {rtNode.periodTotals[p]>0?fmt(rtNode.periodTotals[p]):'—'}
          </td>
        ))}
        <td style={{textAlign:'right',padding:'6px 10px',fontWeight:800,color:'#0B3D91',fontSize:12}}>{fmt(rtNode.total)}</td>
        <td style={{textAlign:'right',padding:'6px 10px'}}>
          {rtNode.movement!==0&&<span style={{fontSize:11,fontWeight:700,color:rtNode.movement>0?'#DC2626':'#1A7A4A'}}>{rtNode.movement>0?'+':''}{fmtM(rtNode.movement)}</span>}
        </td>
      </tr>,
      ...(isRTExpanded ? Object.entries(costCodes).map(([cc, ccNode]) => {
        const k2 = ccKey2(sectionPrefix, rt, cc)
        const isCCExpanded = expandedCC.has(k2)
        return [
          // Cost Code row — ccNode.total/periodTotals/movement are
          // a Σ of the Sub Codes below, never recomputed here.
          <tr key={k2} onClick={()=>toggleCC2(sectionPrefix, rt, cc)} style={{cursor:'pointer',background:'#FDFCFA',borderBottom:'1px solid #F3F4F6'}}
            onMouseEnter={e=>e.currentTarget.style.background='#F5F1EA'}
            onMouseLeave={e=>e.currentTarget.style.background='#FDFCFA'}>
            <td style={{position:'sticky',left:0,zIndex:20,background:'inherit',padding:'9px 12px 9px 24px',fontWeight:700,fontSize:12,color:'#1C1A18',borderRight:'2px solid #E2DCD1',whiteSpace:'nowrap',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis'}}>
              <span style={{marginRight:6,color:'#9A948C',fontSize:10}}>{isCCExpanded?'▼':'▶'}</span>
              {cc}
              <span style={{marginLeft:6,color:'#9A948C',fontSize:10}}>({Object.keys(ccNode.subCodes||{}).length})</span>
            </td>
            <td style={{position:'sticky',left:180,zIndex:20,background:'inherit',padding:'9px 12px',fontSize:11,color:'#5A5650',borderRight:'2px solid #E2DCD1',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {ccNode.desc||'—'}
            </td>
            <td style={{position:'sticky',left:320,zIndex:20,background:'inherit',padding:'9px 8px',borderRight:'2px solid #DDD9D0'}}/>
            {activePeriods.map(p=>(
              <td key={p} style={{textAlign:'right',padding:'9px 10px',fontWeight:600,color:'#1C1A18',fontSize:12}}>
                {ccNode.periodTotals[p]>0?fmt(ccNode.periodTotals[p]):'—'}
              </td>
            ))}
            <td style={{textAlign:'right',padding:'9px 10px',fontWeight:800,color:'#0B3D91',fontSize:12}}>{fmt(ccNode.total)}</td>
            <td style={{textAlign:'right',padding:'9px 10px'}}>
              {ccNode.movement!==0&&<span style={{fontSize:11,fontWeight:700,color:ccNode.movement>0?'#DC2626':'#1A7A4A'}}>{ccNode.movement>0?'+':''}{fmtM(ccNode.movement)}</span>}
            </td>
          </tr>,
          // Sub Code rows — leaf level, reads subNode values
          // directly (each already a Σ over that Sub Code's own
          // rows across sources, from buildHierarchyFromRows()).
          ...(isCCExpanded ? Object.entries(ccNode.subCodes||{}).map(([sc, scNode]) => {
            const src2 = SOURCE_COLORS[scNode.source]||{bg:'#F3F4F6',text:'#374151'}
            return (
              <tr key={`${k2}||${sc}`} style={{background:'#fff',borderBottom:'1px solid #F9FAFB'}}>
                <td style={{position:'sticky',left:0,zIndex:20,background:'#fff',padding:'7px 12px 7px 40px',fontSize:11,color:'#2E2C29',borderRight:'2px solid #E2DCD1',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {sc}
                </td>
                <td style={{position:'sticky',left:180,zIndex:20,background:'#fff',padding:'7px 12px',fontSize:11,color:'#5A5650',borderRight:'2px solid #E2DCD1',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {scNode.desc||'—'}
                </td>
                <td style={{position:'sticky',left:320,zIndex:20,background:'#fff',padding:'7px 8px',borderRight:'2px solid #DDD9D0'}}>
                  <span style={{background:src2.bg,color:src2.text,padding:'1px 6px',borderRadius:4,fontSize:9,fontWeight:700,whiteSpace:'nowrap'}}>{scNode.source}</span>
                </td>
                {activePeriods.map(p=>(
                  <td key={p} style={{textAlign:'right',padding:'7px 10px',color:'#5A5650',fontSize:11}}>
                    {scNode.periodTotals[p]>0?fmt(scNode.periodTotals[p]):'—'}
                  </td>
                ))}
                <td style={{textAlign:'right',padding:'7px 10px',fontWeight:700,color:'#1C1A18',fontSize:11}}>{fmt(scNode.total)}</td>
                <td style={{textAlign:'right',padding:'7px 10px'}}>
                  {scNode.movement!==0&&<span style={{fontSize:10,fontWeight:700,color:scNode.movement>0?'#DC2626':'#1A7A4A'}}>{scNode.movement>0?'+':''}{fmtM(scNode.movement)}</span>}
                </td>
              </tr>
            )
          }) : [])
        ]
      }) : [])
    ]
  })

  // Label text is the only thing that differs between grouping modes in
  // the rendering path below — never a different code path. Direct's
  // first-level label follows hierarchyGroupBy; Indirect's is always
  // "Category" (it has no Resource Type/Resource Code concept).
  const GROUPING_LABEL = { resourceType: 'Resource Type', resourceCode: 'Resource Code' }
  const hierarchyGroupLabel = GROUPING_LABEL[hierarchyGroupBy] || hierarchyGroupBy

  const FREEZE_W = 320 // width of frozen left columns

  return (
    <div className="flex flex-col h-screen bg-[#F5F1EA]">

      {/* Header */}
      <div className="bg-white border-b border-[#E2DCD1] px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#1C1A18]">Monthly Actual Cost</h1>
            <p className="text-sm text-[#5A5650] mt-0.5">IPC · Payroll · Store · Petty Cash · Accrual</p>
          </div>
          <select value={selectedProject?.id||''} onChange={e=>setSelectedProject(projects.find(p=>p.id===e.target.value)||null)}
            className="text-sm border border-[#E2DCD1] rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-[#0B3D91] min-w-48">
            <option value="">— Select Project —</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {/* Period cards */}
        {periods.length > 0 && !loading && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#EDE7DA] overflow-x-auto pb-1">
            <span className="text-xs text-[#5A5650] flex-shrink-0">Filter:</span>
            {periods.map(p => {
              const active = selectedPeriods.size===0 || selectedPeriods.has(p)
              return (
                <button key={p} onClick={()=>togglePeriod(p)}
                  className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-center min-w-[80px] border transition-all ${active&&selectedPeriods.size>0?'bg-[#0B3D91] text-white border-[#0B3D91]':selectedPeriods.size===0?'bg-[#F5F1EA] border-[#E2DCD1] text-[#1C1A18]':'bg-white border-[#E2DCD1] text-[#9A948C]'}`}>
                  <div className="text-xs font-medium">{p}</div>
                  <div className="text-xs font-bold">{fmtM(grandTotals[p]||0)}</div>
                </button>
              )
            })}
            {selectedPeriods.size>0 && <button onClick={()=>setSelectedPeriods(new Set())} className="text-xs text-[#0B3D91] hover:underline flex-shrink-0">Clear</button>}
            <div className="flex-shrink-0 bg-[#0B3D91] rounded-lg px-3 py-1.5 text-center min-w-[80px] ml-2">
              <div className="text-xs text-blue-200">Total</div>
              <div className="text-xs font-bold text-white">{fmtM(grandTotal)}</div>
            </div>
          </div>
        )}
      </div>

      {/* ══════ Summary bar — sticky executive summary, reads result.summary
           directly (Phase B, buildSummary()). No calculation happens here:
           every figure below is a value already computed in
           monthlyActualCost.js and handed over as-is. This lets users see
           the overall picture before scrolling through the full hierarchy
           below. ══════ */}
      {summary && !loading && summary.periodsCount > 0 && (
        <div className="bg-white border-b border-[#E2DCD1] px-6 py-3 flex-shrink-0 sticky top-0 z-40 shadow-sm">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-xs font-semibold text-[#5A5650] uppercase tracking-wide">Summary</span>
            <div className="flex items-center gap-2 bg-[#F5F1EA] rounded-lg px-3 py-1.5">
              <span className="text-xs text-[#5A5650]">Grand Total</span>
              <span className="text-sm font-bold text-[#1C1A18]">{fmt(summary.grandTotal)}</span>
            </div>
            <div className="flex items-center gap-2 bg-[#F5F1EA] rounded-lg px-3 py-1.5">
              <span className="text-xs text-[#5A5650]">{monthLabel(summary.lastPeriod)}</span>
              <span className="text-sm font-bold text-[#1C1A18]">{fmt(summary.lastPeriodTotal)}</span>
            </div>
            {summary.prevPeriod && (
              <div className="flex items-center gap-2 bg-[#F5F1EA] rounded-lg px-3 py-1.5">
                <span className="text-xs text-[#5A5650]">{monthLabel(summary.prevPeriod)}</span>
                <span className="text-sm font-bold text-[#1C1A18]">{fmt(summary.prevPeriodTotal)}</span>
              </div>
            )}
            <div className="flex items-center gap-2 bg-[#F5F1EA] rounded-lg px-3 py-1.5">
              <span className="text-xs text-[#5A5650]">Movement</span>
              <span className={`text-sm font-bold ${summary.grandMovement>0?'text-[#DC2626]':summary.grandMovement<0?'text-[#1A7A4A]':'text-[#1C1A18]'}`}>
                {summary.grandMovement>0?'+':''}{fmt(summary.grandMovement)}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-[#F5F1EA] rounded-lg px-3 py-1.5">
              <span className="text-xs text-[#5A5650]">Periods</span>
              <span className="text-sm font-bold text-[#1C1A18]">{summary.periodsCount}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters bar */}
      {selectedProject && (
        <div className="bg-white border-b border-[#E2DCD1] px-6 py-2 flex items-center gap-3 flex-shrink-0">
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search code or description…"
            className="text-sm border border-[#E2DCD1] rounded-xl px-3 py-1.5 focus:outline-none focus:border-[#0B3D91] bg-[#F5F1EA] w-64"/>
          <div className="flex items-center border border-[#E2DCD1] rounded-xl overflow-hidden flex-shrink-0">
            <button onClick={()=>setViewMode('source')}
              className={`text-xs px-3 py-1.5 font-medium ${viewMode==='source'?'bg-[#0B3D91] text-white':'bg-white text-[#5A5650] hover:bg-[#F5F1EA]'}`}>
              By Source
            </button>
            <button onClick={()=>{setViewMode('hierarchy'); setHierarchyGroupBy('resourceType')}}
              className={`text-xs px-3 py-1.5 font-medium ${viewMode==='hierarchy'&&hierarchyGroupBy==='resourceType'?'bg-[#0B3D91] text-white':'bg-white text-[#5A5650] hover:bg-[#F5F1EA]'}`}>
              By Resource Type
            </button>
            <button onClick={()=>{setViewMode('hierarchy'); setHierarchyGroupBy('resourceCode')}}
              className={`text-xs px-3 py-1.5 font-medium ${viewMode==='hierarchy'&&hierarchyGroupBy==='resourceCode'?'bg-[#0B3D91] text-white':'bg-white text-[#5A5650] hover:bg-[#F5F1EA]'}`}>
              By Resource Code
            </button>
          </div>
          <button onClick={toggleAll} className="text-xs px-3 py-1.5 border border-[#E2DCD1] rounded-xl text-[#5A5650] hover:bg-[#F5F1EA]">
            {showAll ? 'Collapse All' : 'Expand All'}
          </button>
          <span className="text-xs text-[#5A5650]">{activePeriods.length} period{activePeriods.length!==1?'s':''} · {rows.length} lines</span>
          <div className="flex-1"/>
          <button onClick={()=>setPrintMode('current')} disabled={!periods.length}
            className="text-xs px-3 py-1.5 border border-[#0B3D91] rounded-xl text-[#0B3D91] hover:bg-[#EFF6FF] disabled:opacity-40 disabled:cursor-not-allowed font-medium">
            🖨 Current Month Report
          </button>
          <button onClick={()=>setPrintMode('all')} disabled={!periods.length}
            className="text-xs px-3 py-1.5 border border-[#0B3D91] rounded-xl text-[#0B3D91] hover:bg-[#EFF6FF] disabled:opacity-40 disabled:cursor-not-allowed font-medium">
            🖨 All Months Report
          </button>
          <button onClick={()=>setPrintMode('total')} disabled={!periods.length}
            className="text-xs px-3 py-1.5 border border-[#0B3D91] rounded-xl text-[#0B3D91] hover:bg-[#EFF6FF] disabled:opacity-40 disabled:cursor-not-allowed font-medium">
            🖨 Total Summary
          </button>
        </div>
      )}

      {/* Table — fills remaining height with scroll */}
      <div className="flex-1 overflow-hidden px-6 py-4">
        {!selectedProject ? (
          <div className="flex items-center justify-center h-full text-[#5A5650]">Select a project</div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full text-[#5A5650]">
            <div className="w-5 h-5 border-2 border-[#0B3D91]/30 border-t-[#0B3D91] rounded-full animate-spin mr-2"/>Loading…
          </div>
        ) : rows.length===0 ? (
          <div className="flex items-center justify-center h-full text-[#5A5650]">No data — update actual dates first</div>
        ) : viewMode === 'hierarchy' ? (
          <div className="bg-white rounded-2xl border border-[#E2DCD1] shadow-sm h-full overflow-auto">
            <table className="text-sm border-collapse" style={{minWidth: FREEZE_W + 80 + activePeriods.length*110 + 220}}>
              <thead style={{position:'sticky',top:0,zIndex:30}}>
                <tr className="bg-[#F5F1EA]">
                  <th style={{position:'sticky',left:0,zIndex:31,background:'#F5F1EA',minWidth:180,maxWidth:200,textAlign:'left',padding:'10px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#5A5650',borderRight:'2px solid #E2DCD1',whiteSpace:'nowrap'}}>
                    {hierarchyGroupLabel} / Cost Code / Sub Code
                  </th>
                  <th style={{position:'sticky',left:180,zIndex:31,background:'#F5F1EA',minWidth:140,maxWidth:160,textAlign:'left',padding:'10px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#5A5650',borderRight:'2px solid #E2DCD1',whiteSpace:'nowrap'}}>
                    Description
                  </th>
                  <th style={{position:'sticky',left:320,zIndex:31,background:'#F5F1EA',width:80,textAlign:'left',padding:'10px 8px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#5A5650',borderRight:'2px solid #DDD9D0',whiteSpace:'nowrap'}}>
                    Source
                  </th>
                  {activePeriods.map(p=>(
                    <th key={p} style={{minWidth:100,textAlign:'right',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#5A5650',whiteSpace:'nowrap',background:'#F5F1EA'}}>
                      {p}
                    </th>
                  ))}
                  <th style={{minWidth:110,textAlign:'right',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#0B3D91',whiteSpace:'nowrap',background:'#F5F1EA'}}>Total</th>
                  <th style={{minWidth:90,textAlign:'right',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#5A5650',whiteSpace:'nowrap',background:'#F5F1EA'}}>Movement</th>
                </tr>
              </thead>
              <tbody>
                {/* Direct section header — Direct and Indirect are two
                    sections within the SAME grouping mode (not separate
                    tabs). renderGroupEntries() below is called once per
                    section with the exact same rendering logic — no
                    Indirect-specific branch exists anywhere in it. */}
                {filteredDirectHierarchy.length > 0 && (
                  <tr style={{background:'#E8ECF7'}}>
                    <td colSpan={activePeriods.length+5} style={{padding:'5px 12px',fontWeight:800,fontSize:10,color:'#374151',textTransform:'uppercase',letterSpacing:'.08em',position:'sticky',left:0}}>Direct</td>
                  </tr>
                )}
                {renderGroupEntries(filteredDirectHierarchy, 'direct')}

                {/* Indirect section header — Indirect's hierarchy is
                    Category -> Cost Code -> Sub Code (no Resource Type/
                    Resource Code level), reusing Cost Control's own
                    categorization verbatim. Same renderGroupEntries() call
                    as Direct above — identical rendering path. */}
                {filteredIndirectHierarchy.length > 0 && (
                  <tr style={{background:'#F3EDFA'}}>
                    <td colSpan={activePeriods.length+5} style={{padding:'5px 12px',fontWeight:800,fontSize:10,color:'#5B21B6',textTransform:'uppercase',letterSpacing:'.08em',position:'sticky',left:0}}>Indirect</td>
                  </tr>
                )}
                {renderGroupEntries(filteredIndirectHierarchy, 'indirect')}

                {/* Grand Total — reads the SAME grandTotals/grandTotal/
                    grandMovement/cumulativeTotals used by the By Source view
                    above; the Resource Type hierarchy never introduces its
                    own grand-total figure. */}
                <tr style={{background:'#1C1A18',borderTop:'3px solid #0B3D91'}}>
                  <td colSpan={3} style={{position:'sticky',left:0,zIndex:20,background:'#1C1A18',padding:'10px 12px',fontWeight:700,fontSize:12,color:'#fff',textTransform:'uppercase',letterSpacing:'.06em',borderRight:'2px solid #374151'}}>
                    Grand Total
                  </td>
                  {activePeriods.map(p=>(
                    <td key={p} style={{textAlign:'right',padding:'10px',fontWeight:700,color:'#fff',fontSize:12}}>
                      {grandTotals[p]>0?fmt(grandTotals[p]):'—'}
                    </td>
                  ))}
                  <td style={{textAlign:'right',padding:'10px',fontWeight:800,color:'#1A7A4A',fontSize:13}}>{fmt(grandTotal)}</td>
                  <td style={{textAlign:'right',padding:'10px 10px'}}>
                    {grandMovement!==0&&<span style={{fontSize:11,fontWeight:700,color:grandMovement>0?'#DC2626':'#1A7A4A'}}>{grandMovement>0?'+':''}{fmtM(grandMovement)}</span>}
                  </td>
                </tr>
                <tr style={{background:'#0B3D91'}}>
                  <td colSpan={3} style={{position:'sticky',left:0,zIndex:20,background:'#0B3D91',padding:'10px 12px',fontWeight:700,fontSize:12,color:'#fff',textTransform:'uppercase',letterSpacing:'.06em',borderRight:'2px solid #1D4ED8'}}>
                    Cumulative
                  </td>
                  {activePeriods.map(p=>(
                    <td key={p} style={{textAlign:'right',padding:'10px',fontWeight:700,color:'#BFDBFE',fontSize:12}}>
                      {fmt(cumulativeTotals[p])}
                    </td>
                  ))}
                  <td style={{textAlign:'right',padding:'10px',fontWeight:800,color:'#fff',fontSize:13}}>{fmt(grandTotal)}</td>
                  <td/>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#E2DCD1] shadow-sm h-full overflow-auto">
            <table className="text-sm border-collapse" style={{minWidth: FREEZE_W + 80 + activePeriods.length*110 + 220}}>
              <thead style={{position:'sticky',top:0,zIndex:30}}>
                <tr className="bg-[#F5F1EA]">
                  {/* Frozen col 1: Cost Code/Sub Code */}
                  <th style={{position:'sticky',left:0,zIndex:31,background:'#F5F1EA',minWidth:180,maxWidth:200,textAlign:'left',padding:'10px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#5A5650',borderRight:'2px solid #E2DCD1',whiteSpace:'nowrap'}}>
                    Cost Code / Sub Code
                  </th>
                  {/* Frozen col 2: Description */}
                  <th style={{position:'sticky',left:180,zIndex:31,background:'#F5F1EA',minWidth:140,maxWidth:160,textAlign:'left',padding:'10px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#5A5650',borderRight:'2px solid #E2DCD1',whiteSpace:'nowrap'}}>
                    Description
                  </th>
                  {/* Frozen col 3: Source */}
                  <th style={{position:'sticky',left:320,zIndex:31,background:'#F5F1EA',width:80,textAlign:'left',padding:'10px 8px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#5A5650',borderRight:'2px solid #DDD9D0',whiteSpace:'nowrap'}}>
                    Source
                  </th>
                  {/* Period columns */}
                  {activePeriods.map(p=>(
                    <th key={p} style={{minWidth:100,textAlign:'right',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#5A5650',whiteSpace:'nowrap',background:'#F5F1EA'}}>
                      {p}
                    </th>
                  ))}
                  <th style={{minWidth:110,textAlign:'right',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#0B3D91',whiteSpace:'nowrap',background:'#F5F1EA'}}>Total</th>
                  <th style={{minWidth:90,textAlign:'right',padding:'10px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',color:'#5A5650',whiteSpace:'nowrap',background:'#F5F1EA'}}>Movement</th>
                </tr>
              </thead>
              <tbody>
                {filteredSources.map(([src, codes]) => {
                  const srcColors = SOURCE_COLORS[src] || {bg:'#F3F4F6',text:'#374151'}
                  // Source header row
                  const srcTotal = rows.filter(r=>r.source===src).reduce((s,r)=>s+activePeriods.reduce((ss,p)=>ss+(r.periods[p]||0),0),0)
                  return [
                    <tr key={`src-${src}`} style={{background:'#F0F4FF',borderTop:'2px solid #E2DCD1'}}>
                      <td colSpan={3} style={{position:'sticky',left:0,zIndex:20,background:'#F0F4FF',padding:'6px 12px',fontWeight:700,fontSize:12,color:'#0B3D91',borderRight:'2px solid #DDD9D0'}}>
                        <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                          <span style={{background:srcColors.bg,color:srcColors.text,padding:'1px 8px',borderRadius:6,fontSize:10,fontWeight:700}}>{src}</span>
                        </span>
                      </td>
                      {activePeriods.map(p => {
                        const pTotal = rows.filter(r=>r.source===src).reduce((s,r)=>s+(r.periods[p]||0),0)
                        return <td key={p} style={{textAlign:'right',padding:'6px 10px',fontWeight:700,color:'#0B3D91',fontSize:12}}>{pTotal>0?fmt(pTotal):'—'}</td>
                      })}
                      <td style={{textAlign:'right',padding:'6px 10px',fontWeight:800,color:'#0B3D91',fontSize:12}}>{fmt(srcTotal)}</td>
                      <td/>
                    </tr>,
                    ...Object.entries(codes).map(([cc, {desc, subs}]) => {
                      const k = expandKey(src, cc)
                      const isExpanded = expandedCodes.has(k)
                      const ccPeriodTotals = {}
                      activePeriods.forEach(p => { ccPeriodTotals[p] = subs.reduce((s,r)=>s+(r.periods[p]||0),0) })
                      const ccTotal = Object.values(ccPeriodTotals).reduce((s,v)=>s+v,0)
                      const lastP = activePeriods[activePeriods.length-1]
                      const prevP = activePeriods[activePeriods.length-2]
                      const movement = lastP ? (ccPeriodTotals[lastP]||0)-(prevP?ccPeriodTotals[prevP]||0:0) : 0

                      return [
                        // Cost code row
                        <tr key={k} onClick={()=>toggleExpand(src,cc)} style={{cursor:'pointer',background:'#FDFCFA',borderBottom:'1px solid #F3F4F6'}}
                          onMouseEnter={e=>e.currentTarget.style.background='#F5F1EA'}
                          onMouseLeave={e=>e.currentTarget.style.background='#FDFCFA'}>
                          <td style={{position:'sticky',left:0,zIndex:20,background:'inherit',padding:'9px 12px',fontWeight:700,fontSize:12,color:'#1C1A18',borderRight:'2px solid #E2DCD1',whiteSpace:'nowrap',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis'}}>
                            <span style={{marginRight:6,color:'#9A948C',fontSize:10}}>{isExpanded?'▼':'▶'}</span>
                            {cc}
                            <span style={{marginLeft:6,color:'#9A948C',fontSize:10}}>({subs.length})</span>
                          </td>
                          <td style={{position:'sticky',left:180,zIndex:20,background:'inherit',padding:'9px 12px',fontSize:11,color:'#5A5650',borderRight:'2px solid #E2DCD1',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {desc||'—'}
                          </td>
                          <td style={{position:'sticky',left:320,zIndex:20,background:'inherit',padding:'9px 8px',borderRight:'2px solid #DDD9D0'}}/>
                          {activePeriods.map(p=>(
                            <td key={p} style={{textAlign:'right',padding:'9px 10px',fontWeight:600,color:'#1C1A18',fontSize:12}}>
                              {ccPeriodTotals[p]>0?fmt(ccPeriodTotals[p]):'—'}
                            </td>
                          ))}
                          <td style={{textAlign:'right',padding:'9px 10px',fontWeight:800,color:'#0B3D91',fontSize:12}}>{fmt(ccTotal)}</td>
                          <td style={{textAlign:'right',padding:'9px 10px'}}>
                            {movement!==0&&<span style={{fontSize:11,fontWeight:700,color:movement>0?'#DC2626':'#1A7A4A'}}>{movement>0?'+':''}{fmtM(movement)}</span>}
                          </td>
                        </tr>,
                        // Sub code rows
                        ...(isExpanded ? subs.map(r => {
                          const rowTotal = activePeriods.reduce((s,p)=>s+(r.periods[p]||0),0)
                          const src2 = SOURCE_COLORS[r.source]||{bg:'#F3F4F6',text:'#374151'}
                          return (
                            <tr key={`${k}||${r.subCode}`} style={{background:'#fff',borderBottom:'1px solid #F9FAFB'}}>
                              <td style={{position:'sticky',left:0,zIndex:20,background:'#fff',padding:'7px 12px 7px 28px',fontSize:11,color:'#2E2C29',borderRight:'2px solid #E2DCD1',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                {r.subCode}
                              </td>
                              <td style={{position:'sticky',left:180,zIndex:20,background:'#fff',padding:'7px 12px',fontSize:11,color:'#5A5650',borderRight:'2px solid #E2DCD1',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                {r.costCodeDesc||'—'}
                              </td>
                              <td style={{position:'sticky',left:320,zIndex:20,background:'#fff',padding:'7px 8px',borderRight:'2px solid #DDD9D0'}}>
                                <span style={{background:src2.bg,color:src2.text,padding:'1px 6px',borderRadius:4,fontSize:9,fontWeight:700,whiteSpace:'nowrap'}}>{r.source}</span>
                              </td>
                              {activePeriods.map(p=>(
                                <td key={p} style={{textAlign:'right',padding:'7px 10px',color:'#5A5650',fontSize:11}}>
                                  {r.periods[p]>0?fmt(r.periods[p]):'—'}
                                </td>
                              ))}
                              <td style={{textAlign:'right',padding:'7px 10px',fontWeight:700,color:'#1C1A18',fontSize:11}}>{fmt(rowTotal)}</td>
                              <td/>
                            </tr>
                          )
                        }) : [])
                      ]
                    })
                  ]
                })}

                {/* Grand Total */}
                <tr style={{background:'#1C1A18',borderTop:'3px solid #0B3D91'}}>
                  <td colSpan={3} style={{position:'sticky',left:0,zIndex:20,background:'#1C1A18',padding:'10px 12px',fontWeight:700,fontSize:12,color:'#fff',textTransform:'uppercase',letterSpacing:'.06em',borderRight:'2px solid #374151'}}>
                    Grand Total
                  </td>
                  {activePeriods.map(p=>(
                    <td key={p} style={{textAlign:'right',padding:'10px',fontWeight:700,color:'#fff',fontSize:12}}>
                      {grandTotals[p]>0?fmt(grandTotals[p]):'—'}
                    </td>
                  ))}
                  <td style={{textAlign:'right',padding:'10px',fontWeight:800,color:'#1A7A4A',fontSize:13}}>{fmt(grandTotal)}</td>
                  <td style={{textAlign:'right',padding:'10px 10px'}}>
                    {grandMovement!==0&&<span style={{fontSize:11,fontWeight:700,color:grandMovement>0?'#DC2626':'#1A7A4A'}}>{grandMovement>0?'+':''}{fmtM(grandMovement)}</span>}
                  </td>
                </tr>

                {/* Cumulative */}
                <tr style={{background:'#0B3D91'}}>
                  <td colSpan={3} style={{position:'sticky',left:0,zIndex:20,background:'#0B3D91',padding:'10px 12px',fontWeight:700,fontSize:12,color:'#fff',textTransform:'uppercase',letterSpacing:'.06em',borderRight:'2px solid #1D4ED8'}}>
                    Cumulative
                  </td>
                  {activePeriods.map(p=>(
                    <td key={p} style={{textAlign:'right',padding:'10px',fontWeight:700,color:'#BFDBFE',fontSize:12}}>
                      {fmt(cumulativeTotals[p])}
                    </td>
                  ))}
                  <td style={{textAlign:'right',padding:'10px',fontWeight:800,color:'#fff',fontSize:13}}>{fmt(grandTotal)}</td>
                  <td/>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════ PRINT REPORTS (rendered via Portal directly into <body>, outside any app layout/sidebar) ══════════ */}
      {printMode && createPortal(
        <div className="print-overlay" style={{position:'fixed',inset:0,zIndex:99999,background:'#525659',overflow:'auto'}}>
          <div className="no-print" style={{position:'sticky',top:0,zIndex:10,background:'#323639',padding:'10px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{color:'#fff',fontSize:13,fontWeight:600}}>
              {printMode==='current' ? 'Current Month Actual Cost Report' : printMode==='all' ? 'All Months Actual Cost Report' : 'Total Summary Report'}
            </span>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {printMode==='current' && (
                <select value={selectedMonth||''} onChange={e=>setSelectedMonth(e.target.value||null)}
                  style={{padding:'6px 10px',fontSize:11,fontWeight:600,borderRadius:6,border:'1px solid #fff',background:'#fff',color:'#323639',cursor:'pointer'}}>
                  {periods.map(p=><option key={p} value={p}>{monthLabel(p)}</option>)}
                </select>
              )}
              {printMode==='current' && (
                <div className="no-print" style={{display:'flex',border:'1px solid #fff',borderRadius:6,overflow:'hidden'}}>
                  <button onClick={()=>setCurrentGroupBy('sub')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:currentGroupBy==='sub'?'#fff':'transparent',color:currentGroupBy==='sub'?'#323639':'#fff'}}>Sub Code</button>
                  <button onClick={()=>setCurrentGroupBy('cost')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:currentGroupBy==='cost'?'#fff':'transparent',color:currentGroupBy==='cost'?'#323639':'#fff'}}>Cost Code</button>
                  <button onClick={()=>setCurrentGroupBy('resourceType')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:currentGroupBy==='resourceType'?'#fff':'transparent',color:currentGroupBy==='resourceType'?'#323639':'#fff'}}>Resource Type</button>
                  <button onClick={()=>setCurrentGroupBy('resourceCode')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:currentGroupBy==='resourceCode'?'#fff':'transparent',color:currentGroupBy==='resourceCode'?'#323639':'#fff'}}>Resource Code</button>
                </div>
              )}
              {printMode==='all' && (
                <div className="no-print" style={{display:'flex',border:'1px solid #fff',borderRadius:6,overflow:'hidden'}}>
                  <button onClick={()=>setAllGroupBy('sub')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:allGroupBy==='sub'?'#fff':'transparent',color:allGroupBy==='sub'?'#323639':'#fff'}}>Sub Code</button>
                  <button onClick={()=>setAllGroupBy('cost')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:allGroupBy==='cost'?'#fff':'transparent',color:allGroupBy==='cost'?'#323639':'#fff'}}>Cost Code</button>
                  <button onClick={()=>setAllGroupBy('resourceType')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:allGroupBy==='resourceType'?'#fff':'transparent',color:allGroupBy==='resourceType'?'#323639':'#fff'}}>Resource Type</button>
                  <button onClick={()=>setAllGroupBy('resourceCode')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:allGroupBy==='resourceCode'?'#fff':'transparent',color:allGroupBy==='resourceCode'?'#323639':'#fff'}}>Resource Code</button>
                </div>
              )}
              {printMode==='total' && (
                <div className="no-print" style={{display:'flex',border:'1px solid #fff',borderRadius:6,overflow:'hidden'}}>
                  <button onClick={()=>setTotalGroupBy('sub')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:totalGroupBy==='sub'?'#fff':'transparent',color:totalGroupBy==='sub'?'#323639':'#fff'}}>Sub Code</button>
                  <button onClick={()=>setTotalGroupBy('cost')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:totalGroupBy==='cost'?'#fff':'transparent',color:totalGroupBy==='cost'?'#323639':'#fff'}}>Cost Code</button>
                  <button onClick={()=>setTotalGroupBy('resourceType')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:totalGroupBy==='resourceType'?'#fff':'transparent',color:totalGroupBy==='resourceType'?'#323639':'#fff'}}>Resource Type</button>
                  <button onClick={()=>setTotalGroupBy('resourceCode')} style={{padding:'6px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:totalGroupBy==='resourceCode'?'#fff':'transparent',color:totalGroupBy==='resourceCode'?'#323639':'#fff'}}>Resource Code</button>
                </div>
              )}
              <button onClick={()=>window.print()} style={{background:'#0B3D91',color:'#fff',border:'none',borderRadius:6,padding:'7px 16px',fontSize:12,fontWeight:600,cursor:'pointer'}}>Print / Save as PDF</button>
              <button onClick={()=>setPrintMode(null)} style={{background:'#fff',color:'#323639',border:'none',borderRadius:6,padding:'7px 16px',fontSize:12,fontWeight:600,cursor:'pointer'}}>Close</button>
            </div>
          </div>

          <div className="print-page" style={{background:'#fff',maxWidth:printMode==='all'?'96vw':1000,margin:'20px auto',padding:printMode==='all'?'20px 22px':'32px 36px',boxShadow:'0 0 12px rgba(0,0,0,.3)'}}>
            {printMode === 'current' ? (
              <CurrentMonthReport project={selectedProject} rows={rows} periods={periods} groupBy={currentGroupBy} selectedMonth={selectedMonth} groupingsByPeriod={groupingsByPeriod} grandTotals={grandTotals} summary={summary}/>
            ) : printMode === 'all' ? (
              <AllMonthsReport project={selectedProject} rows={rows} periods={activePeriods} groupBy={allGroupBy} groupings={groupings} summary={summary}/>
            ) : (
              <TotalSummaryReport project={selectedProject} rows={rows} periods={periods} groupBy={totalGroupBy} groupings={groupings} grandTotal={grandTotal} summary={summary}/>
            )}
          </div>

          <style>{`
            @media print {
              html, body { height: auto !important; overflow: visible !important; margin:0; padding:0; }
              body > *:not(.print-overlay) { display: none !important; }
              .print-overlay {
                position: static !important;
                inset: auto !important;
                background: #fff !important;
                overflow: visible !important;
                z-index: auto !important;
              }
              .no-print { display: none !important; }
              .print-page {
                box-shadow: none !important;
                margin: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                padding: 0 !important;
              }
              @page { size: ${printMode==='all' ? 'A3' : 'A4'} landscape; margin: ${printMode==='all' ? '10mm' : '12mm'}; }
            }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// PrintSummaryBar — shared across all three print reports. Purely
// presentational: every value it renders (grandTotal, lastPeriodTotal,
// prevPeriodTotal, grandMovement, periodsCount) is read as-is from
// `summary` (report.summary, built by buildSummary() in the data layer —
// see monthlyActualCost.js's own doc-comment for its architecture
// contract). No calculation, no formatting-specific totals, nothing
// re-derived from `rows` here — this is the on-screen sticky Summary bar
// (Phase C), reused verbatim for print output so users see the overall
// picture before scrolling through the detailed hierarchy below.
// ─────────────────────────────────────────────────────────
function PrintSummaryBar({ summary }) {
  if (!summary || !summary.periodsCount) return null
  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:10,marginBottom:16,padding:'10px 12px',background:'#F5F1EA',borderRadius:8,border:'1px solid #E2DCD1'}}>
      <div style={{display:'flex',flexDirection:'column',minWidth:100}}>
        <span style={{fontSize:8.5,color:'#5A5650',textTransform:'uppercase',letterSpacing:'.04em'}}>Grand Total</span>
        <span style={{fontSize:13,fontWeight:800,color:'#1C1A18'}}>{fmt(summary.grandTotal)}</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',minWidth:100}}>
        <span style={{fontSize:8.5,color:'#5A5650',textTransform:'uppercase',letterSpacing:'.04em'}}>{monthLabel(summary.lastPeriod)}</span>
        <span style={{fontSize:13,fontWeight:800,color:'#1C1A18'}}>{fmt(summary.lastPeriodTotal)}</span>
      </div>
      {summary.prevPeriod && (
        <div style={{display:'flex',flexDirection:'column',minWidth:100}}>
          <span style={{fontSize:8.5,color:'#5A5650',textTransform:'uppercase',letterSpacing:'.04em'}}>{monthLabel(summary.prevPeriod)}</span>
          <span style={{fontSize:13,fontWeight:800,color:'#1C1A18'}}>{fmt(summary.prevPeriodTotal)}</span>
        </div>
      )}
      <div style={{display:'flex',flexDirection:'column',minWidth:100}}>
        <span style={{fontSize:8.5,color:'#5A5650',textTransform:'uppercase',letterSpacing:'.04em'}}>Movement</span>
        <span style={{fontSize:13,fontWeight:800,color:summary.grandMovement>0?'#DC2626':summary.grandMovement<0?'#1A7A4A':'#1C1A18'}}>
          {summary.grandMovement>0?'+':''}{fmt(summary.grandMovement)}
        </span>
      </div>
      <div style={{display:'flex',flexDirection:'column',minWidth:70}}>
        <span style={{fontSize:8.5,color:'#5A5650',textTransform:'uppercase',letterSpacing:'.04em'}}>Periods</span>
        <span style={{fontSize:13,fontWeight:800,color:'#1C1A18'}}>{summary.periodsCount}</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Report 1: Current Month Actual Cost (movement = last period's amount)
// ─────────────────────────────────────────────────────────
function CurrentMonthReport({ project, rows, periods, groupBy='sub', selectedMonth, groupingsByPeriod={ resourceType: {}, resourceCode: {} }, grandTotals={}, summary=null }) {
  const lastPeriod = selectedMonth || periods[periods.length - 1]
  const withAmt = rows
    .filter(r => (r.periods[lastPeriod] || 0) > 0)
    .map(r => ({ ...r, amount: r.periods[lastPeriod] || 0 }))

  // Resource Type and Resource Code are peers: same rendering path, same
  // totals/movement, same expand behavior — the ONLY difference is which
  // pre-built tree groupingsByPeriod[groupBy] resolves to (report.
  // groupingsByPeriod.resourceType[lastPeriod] or .resourceCode[
  // lastPeriod]), both produced by the exact same buildHierarchyFromRows()
  // call in the data layer, differing only in which classifier was
  // passed in there. No calculation happens in this component — every
  // number below is read as-is, including the Grand Total, which is
  // grandTotals[lastPeriod] — already computed by
  // buildMonthlyActualCostData() — rather than re-summed here from the
  // grouping nodes (Reconciliation Invariant guarantees the two are
  // always equal, but only one of them is the presentation layer's job
  // to read).
  //
  // Direct and Indirect render as two sections within this SAME report —
  // renderGroupTables() below is the exact same rendering logic called
  // once per section, never a different implementation for Indirect.
  if (groupBy === 'resourceType' || groupBy === 'resourceCode') {
    const groupLabel = groupBy === 'resourceCode' ? 'Resource Code' : 'Resource Type'
    const monthHierarchy = (groupingsByPeriod[groupBy] || {})[lastPeriod] || { Direct: {}, Indirect: {} }
    const directGroups = Object.entries(monthHierarchy.Direct || {}).sort((a,b) => a[0].localeCompare(b[0]))
    const indirectGroups = Object.entries(monthHierarchy.Indirect || {}).sort((a,b) => a[0].localeCompare(b[0]))
    const grandTotal = grandTotals[lastPeriod] || 0

    const renderGroupTables = (groups) => groups.map(([rt, rtNode]) => (
      <table key={rt} style={{width:'100%',borderCollapse:'collapse',fontSize:11,marginBottom:14,pageBreakInside:'avoid'}}>
        <thead>
          <tr style={{background:'#F0F4FF'}}>
            <th colSpan={4} style={{textAlign:'left',padding:'7px 10px',fontSize:11,fontWeight:800,color:'#0B3D91',borderBottom:'2px solid #DDD9D0'}}>
              {rt}
            </th>
          </tr>
          <tr style={{background:'#F5F1EA'}}>
            <th style={{textAlign:'left',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'1px solid #DDD9D0'}}>Sub Code</th>
            <th style={{textAlign:'left',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'1px solid #DDD9D0'}}>Description</th>
            <th style={{textAlign:'left',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'1px solid #DDD9D0',whiteSpace:'nowrap'}}>Source</th>
            <th style={{textAlign:'right',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'1px solid #DDD9D0'}}>Actual Cost</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(rtNode.costCodes).sort((a,b)=>a[0].localeCompare(b[0])).map(([cc, ccNode]) => (
            Object.entries(ccNode.subCodes).sort((a,b)=>a[0].localeCompare(b[0])).map(([sc, scNode]) => (
              <tr key={`${cc}||${sc}`} style={{borderBottom:'1px solid #F0EDE6'}}>
                <td style={{padding:'5px 10px',fontWeight:600}}>{sc}</td>
                <td style={{padding:'5px 10px',color:'#5A5650'}}>{scNode.desc || '—'}</td>
                <td style={{padding:'5px 10px',whiteSpace:'nowrap'}}>{scNode.source}</td>
                <td style={{padding:'5px 10px',textAlign:'right',fontWeight:700}}>{fmt(scNode.total)}</td>
              </tr>
            ))
          ))}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid #0B3D91'}}>
            <td colSpan={3} style={{padding:'6px 10px',fontWeight:700,textTransform:'uppercase',fontSize:10,color:'#0B3D91'}}>{rt} Subtotal</td>
            <td style={{padding:'6px 10px',textAlign:'right',fontWeight:800,fontSize:11,color:'#0B3D91'}}>{fmt(rtNode.total)}</td>
          </tr>
        </tfoot>
      </table>
    ))

    return (
      <div>
        <div style={{textAlign:'center',marginBottom:18,borderBottom:'2px solid #1C1A18',paddingBottom:12}}>
          <div style={{fontSize:18,fontWeight:800,color:'#1C1A18'}}>{project?.name || 'Project'}</div>
          <div style={{fontSize:14,fontWeight:600,color:'#0B3D91',marginTop:4}}>Current Month Actual Cost Report — by {groupLabel}</div>
          <div style={{fontSize:12,color:'#5A5650',marginTop:2}}>
            This is the Actual Cost for <b>{monthLabel(lastPeriod)}</b>
          </div>
        </div>
        <PrintSummaryBar summary={summary}/>
        {directGroups.length === 0 && indirectGroups.length === 0 ? (
          <div style={{padding:20,textAlign:'center',color:'#9A948C',fontSize:11}}>No actual cost recorded for this month</div>
        ) : (
          <>
            {directGroups.length > 0 && (
              <div style={{fontSize:11,fontWeight:800,color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Direct</div>
            )}
            {renderGroupTables(directGroups)}
            {indirectGroups.length > 0 && (
              <div style={{fontSize:11,fontWeight:800,color:'#5B21B6',textTransform:'uppercase',letterSpacing:'.06em',marginTop:10,marginBottom:6}}>Indirect</div>
            )}
            {renderGroupTables(indirectGroups)}
          </>
        )}
        {(directGroups.length + indirectGroups.length) > 0 && (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <tfoot>
              <tr style={{borderTop:'3px solid #1C1A18'}}>
                <td style={{padding:'8px 10px',fontWeight:700,textTransform:'uppercase',fontSize:11}}>Grand Total</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontSize:12}}>{fmt(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        )}
        <div style={{marginTop:24,fontSize:9.5,color:'#9A948C'}}>Printed on {new Date().toLocaleDateString('en-GB')}</div>
      </div>
    )
  }

  let lines, total, cols
  if (groupBy === 'cost') {
    const byCC = {}
    withAmt.forEach(r => {
      const key = r.costCode || '—'
      if (!byCC[key]) byCC[key] = { costCode: key, costCodeDesc: r.costCodeDesc, amount: 0 }
      byCC[key].amount += r.amount
    })
    lines = Object.values(byCC).sort((a,b) => a.costCode.localeCompare(b.costCode))
    cols = ['Cost Code','Description','Actual Cost']
  } else {
    lines = [...withAmt].sort((a,b) => a.subCode.localeCompare(b.subCode))
    cols = ['Sub Code','Description','Source','Actual Cost']
  }
  total = lines.reduce((s,r) => s + r.amount, 0)

  return (
    <div>
      <div style={{textAlign:'center',marginBottom:18,borderBottom:'2px solid #1C1A18',paddingBottom:12}}>
        <div style={{fontSize:18,fontWeight:800,color:'#1C1A18'}}>{project?.name || 'Project'}</div>
        <div style={{fontSize:14,fontWeight:600,color:'#0B3D91',marginTop:4}}>Current Month Actual Cost Report {groupBy==='cost'?'— by Cost Code':'— by Sub Code'}</div>
        <div style={{fontSize:12,color:'#5A5650',marginTop:2}}>
          This is the Actual Cost for <b>{monthLabel(lastPeriod)}</b>
        </div>
      </div>
      <PrintSummaryBar summary={summary}/>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
        <thead>
          <tr style={{background:'#F5F1EA'}}>
            {cols.map((h,i)=>(
              <th key={h} style={{textAlign:i===cols.length-1?'right':'left',padding:'8px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'2px solid #DDD9D0'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((r,i) => (
            <tr key={i} style={{borderBottom:'1px solid #F0EDE6'}}>
              {groupBy==='cost' ? (
                <>
                  <td style={{padding:'6px 10px',fontWeight:600}}>{r.costCode}</td>
                  <td style={{padding:'6px 10px',color:'#5A5650'}}>{r.costCodeDesc || '—'}</td>
                  <td style={{padding:'6px 10px',textAlign:'right',fontWeight:700}}>{fmt(r.amount)}</td>
                </>
              ) : (
                <>
                  <td style={{padding:'6px 10px',fontWeight:600}}>{r.subCode}</td>
                  <td style={{padding:'6px 10px',color:'#5A5650'}}>{r.costCodeDesc || '—'}</td>
                  <td style={{padding:'6px 10px',whiteSpace:'nowrap'}}>{r.source}</td>
                  <td style={{padding:'6px 10px',textAlign:'right',fontWeight:700}}>{fmt(r.amount)}</td>
                </>
              )}
            </tr>
          ))}
          {lines.length === 0 && (
            <tr><td colSpan={cols.length} style={{padding:20,textAlign:'center',color:'#9A948C'}}>No actual cost recorded for this month</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid #1C1A18'}}>
            <td colSpan={cols.length-1} style={{padding:'8px 10px',fontWeight:700,textTransform:'uppercase',fontSize:11}}>Total</td>
            <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontSize:12}}>{fmt(total)}</td>
          </tr>
        </tfoot>
      </table>
      <div style={{marginTop:24,fontSize:9.5,color:'#9A948C'}}>Printed on {new Date().toLocaleDateString('en-GB')}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Report 2: All Months Actual Cost — flat by Sub Code (no cost-code grouping)
// ─────────────────────────────────────────────────────────
function AllMonthsReport({ project, rows, periods, groupBy='sub', groupings={ resourceType: {}, resourceCode: {} }, summary=null }) {
  // Resource Type and Resource Code are peers: same rendering path, same
  // totals — the ONLY difference is which pre-built tree groupings[
  // groupBy] resolves to (report.groupings.resourceType or .resourceCode),
  // both produced by the exact same buildHierarchyFromRows() call in the
  // data layer, differing only in which classifier was passed in there.
  // node.periodTotals[p] is read directly for each selected (visible)
  // period, and node.total as-is for the Total column. `periods` here is
  // activePeriods — a presentation filter that only controls which period
  // columns are shown. It never changes what "Total" means: Total is
  // always the node's full total across every period in the underlying
  // report, exactly as buildHierarchyFromRows() computed it — hiding
  // columns must never change that value. This component performs zero
  // aggregation: every number rendered below is read directly off
  // groupings[groupBy], produced once by the single builder during the
  // original report load.
  if (groupBy === 'resourceType' || groupBy === 'resourceCode') {
    const groupLabel = groupBy === 'resourceCode' ? 'Resource Code' : 'Resource Type'
    const activeGrouping = groupings[groupBy] || { Direct: {}, Indirect: {} }
    const directGroups = Object.entries(activeGrouping.Direct || {}).sort((a,b) => a[0].localeCompare(b[0]))
    const indirectGroups = Object.entries(activeGrouping.Indirect || {}).sort((a,b) => a[0].localeCompare(b[0]))
    const n = periods.length
    const fs = n>18?6.5:n>14?7.5:n>10?8.5:9.5
    const fsHead = fs+0.3
    const pad = n>18?'2px 3px':n>14?'3px 4px':'4px 6px'

    // Direct and Indirect render with this SAME table-building logic,
    // called once per section — no branch anywhere checks which section
    // it's building.
    const renderGroupTables = (groups) => groups.map(([rt, rtNode]) => (
      <table key={rt} style={{width:'100%',borderCollapse:'collapse',fontSize:fs,tableLayout:'fixed',marginBottom:12,pageBreakInside:'avoid'}}>
        <thead>
          <tr style={{background:'#F0F4FF'}}>
            <th colSpan={2+periods.length+1} style={{textAlign:'left',padding:pad,fontSize:fsHead,fontWeight:800,color:'#0B3D91',borderBottom:'2px solid #DDD9D0'}}>{rt}</th>
          </tr>
          <tr style={{background:'#F5F1EA'}}>
            <th style={{textAlign:'left',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#5A5650',borderBottom:'2px solid #DDD9D0'}}>Cost Code</th>
            <th style={{textAlign:'left',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#5A5650',borderBottom:'2px solid #DDD9D0'}}>Description</th>
            {periods.map(p=>(
              <th key={p} style={{textAlign:'right',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#5A5650',borderBottom:'2px solid #DDD9D0',whiteSpace:'nowrap'}}>{p}</th>
            ))}
            <th style={{textAlign:'right',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#0B3D91',borderBottom:'2px solid #DDD9D0'}}>Total</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(rtNode.costCodes).sort((a,b)=>a[0].localeCompare(b[0])).map(([cc, ccNode]) => (
            <tr key={cc} style={{borderBottom:'1px solid #F0EDE6'}}>
              <td style={{padding:pad,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cc}</td>
              <td style={{padding:pad,color:'#5A5650',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ccNode.desc || '—'}</td>
              {periods.map(p=>(
                <td key={p} style={{padding:pad,textAlign:'right'}}>{ccNode.periodTotals[p]>0?fmt(ccNode.periodTotals[p]):'—'}</td>
              ))}
              <td style={{padding:pad,textAlign:'right',fontWeight:700}}>{fmt(ccNode.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid #0B3D91'}}>
            <td colSpan={2} style={{padding:pad,fontWeight:700,textTransform:'uppercase',fontSize:fsHead,color:'#0B3D91'}}>{rt} Subtotal</td>
            {periods.map(p=>(
              <td key={p} style={{padding:pad,textAlign:'right',fontWeight:700,color:'#0B3D91'}}>{fmt(rtNode.periodTotals[p]||0)}</td>
            ))}
            <td style={{padding:pad,textAlign:'right',fontWeight:800,color:'#0B3D91'}}>{fmt(rtNode.total)}</td>
          </tr>
        </tfoot>
      </table>
    ))

    return (
      <div>
        <div style={{textAlign:'center',marginBottom:14,borderBottom:'2px solid #1C1A18',paddingBottom:10}}>
          <div style={{fontSize:16,fontWeight:800,color:'#1C1A18'}}>{project?.name || 'Project'}</div>
          <div style={{fontSize:12,fontWeight:600,color:'#0B3D91',marginTop:3}}>All Actual Cost — Monthly by {groupLabel}</div>
          <div style={{fontSize:10,color:'#5A5650',marginTop:2}}>{periods[0]?monthLabel(periods[0]):''} — {periods[periods.length-1]?monthLabel(periods[periods.length-1]):''}</div>
        </div>
        <PrintSummaryBar summary={summary}/>
        {directGroups.length === 0 && indirectGroups.length === 0 ? (
          <div style={{padding:20,textAlign:'center',color:'#9A948C',fontSize:11}}>No data</div>
        ) : (
          <>
            {directGroups.length > 0 && (
              <div style={{fontSize:fsHead,fontWeight:800,color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Direct</div>
            )}
            {renderGroupTables(directGroups)}
            {indirectGroups.length > 0 && (
              <div style={{fontSize:fsHead,fontWeight:800,color:'#5B21B6',textTransform:'uppercase',letterSpacing:'.06em',marginTop:10,marginBottom:6}}>Indirect</div>
            )}
            {renderGroupTables(indirectGroups)}
          </>
        )}
        <div style={{marginTop:20,fontSize:9,color:'#9A948C'}}>Printed on {new Date().toLocaleDateString('en-GB')}</div>
      </div>
    )
  }

  const totals = {}
  periods.forEach(p => { totals[p] = rows.reduce((s,r)=>s+(r.periods[p]||0),0) })
  const grandTotal = Object.values(totals).reduce((s,v)=>s+v,0)

  let lines, cols
  if (groupBy === 'cost') {
    const byCC = {}
    rows.forEach(r => {
      const key = r.costCode || '—'
      if (!byCC[key]) byCC[key] = { costCode: key, costCodeDesc: r.costCodeDesc, periods: {} }
      periods.forEach(p => { byCC[key].periods[p] = (byCC[key].periods[p]||0) + (r.periods[p]||0) })
    })
    lines = Object.values(byCC).sort((a,b) => a.costCode.localeCompare(b.costCode))
    cols = ['Cost Code','Description']
  } else {
    lines = [...rows].sort((a,b) => a.subCode.localeCompare(b.subCode))
    cols = ['Sub Code','Description','Source']
  }

  const n = periods.length
  const fs = n>18?6.5:n>14?7.5:n>10?8.5:9.5
  const fsHead = fs+0.3
  const pad = n>18?'2px 3px':n>14?'3px 4px':'4px 6px'

  return (
    <div>
      <div style={{textAlign:'center',marginBottom:14,borderBottom:'2px solid #1C1A18',paddingBottom:10}}>
        <div style={{fontSize:16,fontWeight:800,color:'#1C1A18'}}>{project?.name || 'Project'}</div>
        <div style={{fontSize:12,fontWeight:600,color:'#0B3D91',marginTop:3}}>All Actual Cost — Monthly {groupBy==='cost'?'by Cost Code':'by Sub Code'}</div>
        <div style={{fontSize:10,color:'#5A5650',marginTop:2}}>{periods[0]?monthLabel(periods[0]):''} — {periods[periods.length-1]?monthLabel(periods[periods.length-1]):''}</div>
      </div>
      <PrintSummaryBar summary={summary}/>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:fs,tableLayout:'fixed'}}>
        <thead>
          <tr style={{background:'#F5F1EA'}}>
            {cols.map(h=>(
              <th key={h} style={{textAlign:'left',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#5A5650',borderBottom:'2px solid #DDD9D0'}}>{h}</th>
            ))}
            {periods.map(p=>(
              <th key={p} style={{textAlign:'right',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#5A5650',borderBottom:'2px solid #DDD9D0',whiteSpace:'nowrap'}}>{p}</th>
            ))}
            <th style={{textAlign:'right',padding:pad,fontSize:fsHead,fontWeight:700,textTransform:'uppercase',color:'#0B3D91',borderBottom:'2px solid #DDD9D0'}}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((r,i) => {
            const rowTotal = periods.reduce((s,p)=>s+(r.periods[p]||0),0)
            return (
              <tr key={i} style={{borderBottom:'1px solid #F0EDE6'}}>
                {groupBy==='cost' ? (
                  <>
                    <td style={{padding:pad,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.costCode}</td>
                    <td style={{padding:pad,color:'#5A5650',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.costCodeDesc || '—'}</td>
                  </>
                ) : (
                  <>
                    <td style={{padding:pad,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.subCode}</td>
                    <td style={{padding:pad,color:'#5A5650',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.costCodeDesc || '—'}</td>
                    <td style={{padding:pad,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.source}</td>
                  </>
                )}
                {periods.map(p=>(
                  <td key={p} style={{padding:pad,textAlign:'right'}}>{r.periods[p]>0?fmt(r.periods[p]):'—'}</td>
                ))}
                <td style={{padding:pad,textAlign:'right',fontWeight:700}}>{fmt(rowTotal)}</td>
              </tr>
            )
          })}
          {lines.length === 0 && (
            <tr><td colSpan={cols.length+1+periods.length} style={{padding:20,textAlign:'center',color:'#9A948C'}}>No data</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid #1C1A18'}}>
            <td colSpan={cols.length} style={{padding:pad,fontWeight:700,textTransform:'uppercase',fontSize:fsHead}}>Grand Total</td>
            {periods.map(p=>(
              <td key={p} style={{padding:pad,textAlign:'right',fontWeight:700}}>{fmt(totals[p])}</td>
            ))}
            <td style={{padding:pad,textAlign:'right',fontWeight:800,color:'#0B3D91'}}>{fmt(grandTotal)}</td>
          </tr>

        </tfoot>
      </table>
      <div style={{marginTop:20,fontSize:9,color:'#9A948C'}}>Printed on {new Date().toLocaleDateString('en-GB')}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Report 3: Total Summary — one Total Actual Cost column, by Sub Code or Cost Code
// ─────────────────────────────────────────────────────────
function TotalSummaryReport({ project, rows, periods=[], groupBy='sub', groupings={ resourceType: {}, resourceCode: {} }, grandTotal: grandTotalFromReport=0, summary=null }) {
  const lastPeriod = periods[periods.length - 1]

  // Resource Type and Resource Code are peers: same rendering path — the
  // ONLY difference is which pre-built tree groupings[groupBy] resolves
  // to (report.groupings.resourceType or .resourceCode), both produced
  // by the exact same buildHierarchyFromRows() call in the data layer,
  // differing only in which classifier was passed in there. node.total
  // is read directly — no aggregation happens here. Grand Total is read
  // as-is from the data layer's result (buildMonthlyActualCostData) —
  // never re-derived from rows here.
  if (groupBy === 'resourceType' || groupBy === 'resourceCode') {
    const groupLabel = groupBy === 'resourceCode' ? 'Resource Code' : 'Resource Type'
    const activeGrouping = groupings[groupBy] || { Direct: {}, Indirect: {} }
    const directGroups = Object.entries(activeGrouping.Direct || {}).sort((a,b) => a[0].localeCompare(b[0])).filter(([,rt]) => rt.total > 0)
    const indirectGroups = Object.entries(activeGrouping.Indirect || {}).sort((a,b) => a[0].localeCompare(b[0])).filter(([,rt]) => rt.total > 0)

    // Direct and Indirect render with this SAME table-building logic,
    // called once per section — no branch anywhere checks which section
    // it's building.
    const renderGroupTables = (groups) => groups.map(([rt, rtNode]) => (
      <table key={rt} style={{width:'100%',borderCollapse:'collapse',fontSize:11,marginBottom:14,pageBreakInside:'avoid'}}>
        <thead>
          <tr style={{background:'#F0F4FF'}}>
            <th colSpan={3} style={{textAlign:'left',padding:'7px 10px',fontSize:11,fontWeight:800,color:'#0B3D91',borderBottom:'2px solid #DDD9D0'}}>{rt}</th>
          </tr>
          <tr style={{background:'#F5F1EA'}}>
            <th style={{textAlign:'left',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'1px solid #DDD9D0'}}>Sub Code</th>
            <th style={{textAlign:'left',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'1px solid #DDD9D0'}}>Description</th>
            <th style={{textAlign:'right',padding:'6px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#0B3D91',borderBottom:'1px solid #DDD9D0'}}>Total Actual Cost</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(rtNode.costCodes).sort((a,b)=>a[0].localeCompare(b[0])).map(([cc, ccNode]) => (
            Object.entries(ccNode.subCodes).sort((a,b)=>a[0].localeCompare(b[0])).filter(([,sc]) => sc.total > 0).map(([sc, scNode]) => (
              <tr key={`${cc}||${sc}`} style={{borderBottom:'1px solid #F0EDE6'}}>
                <td style={{padding:'5px 10px',fontWeight:600}}>{sc}</td>
                <td style={{padding:'5px 10px',color:'#5A5650'}}>{scNode.desc || '—'}</td>
                <td style={{padding:'5px 10px',textAlign:'right',fontWeight:700}}>{fmt(scNode.total)}</td>
              </tr>
            ))
          ))}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid #0B3D91'}}>
            <td colSpan={2} style={{padding:'6px 10px',fontWeight:700,textTransform:'uppercase',fontSize:10,color:'#0B3D91'}}>{rt} Subtotal</td>
            <td style={{padding:'6px 10px',textAlign:'right',fontWeight:800,fontSize:11,color:'#0B3D91'}}>{fmt(rtNode.total)}</td>
          </tr>
        </tfoot>
      </table>
    ))

    return (
      <div>
        <div style={{textAlign:'center',marginBottom:18,borderBottom:'2px solid #1C1A18',paddingBottom:12}}>
          <div style={{fontSize:18,fontWeight:800,color:'#1C1A18'}}>{project?.name || 'Project'}</div>
          <div style={{fontSize:14,fontWeight:600,color:'#0B3D91',marginTop:4}}>Total Actual Cost Summary — by {groupLabel}</div>
          <div style={{fontSize:11,color:'#5A5650',marginTop:2}}>Cumulative up to <b>{monthLabel(lastPeriod)}</b></div>
        </div>
        <PrintSummaryBar summary={summary}/>
        {directGroups.length === 0 && indirectGroups.length === 0 ? (
          <div style={{padding:20,textAlign:'center',color:'#9A948C',fontSize:11}}>No data</div>
        ) : (
          <>
            {directGroups.length > 0 && (
              <div style={{fontSize:11,fontWeight:800,color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Direct</div>
            )}
            {renderGroupTables(directGroups)}
            {indirectGroups.length > 0 && (
              <div style={{fontSize:11,fontWeight:800,color:'#5B21B6',textTransform:'uppercase',letterSpacing:'.06em',marginTop:10,marginBottom:6}}>Indirect</div>
            )}
            {renderGroupTables(indirectGroups)}
          </>
        )}
        {(directGroups.length + indirectGroups.length) > 0 && (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <tfoot>
              <tr style={{borderTop:'3px solid #1C1A18'}}>
                <td style={{padding:'8px 10px',fontWeight:700,textTransform:'uppercase',fontSize:11}}>Grand Total</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontSize:12}}>{fmt(grandTotalFromReport)}</td>
              </tr>
            </tfoot>
          </table>
        )}
        <div style={{marginTop:24,fontSize:9.5,color:'#9A948C'}}>Printed on {new Date().toLocaleDateString('en-GB')}</div>
      </div>
    )
  }

  let lines, cols
  if (groupBy === 'cost') {
    const byCC = {}
    rows.forEach(r => {
      const key = r.costCode || '—'
      const rowTotal = Object.values(r.periods||{}).reduce((s,v)=>s+v,0)
      if (!byCC[key]) byCC[key] = { costCode: key, costCodeDesc: r.costCodeDesc, total: 0 }
      byCC[key].total += rowTotal
    })
    lines = Object.values(byCC).sort((a,b) => a.costCode.localeCompare(b.costCode))
    cols = ['Cost Code','Description']
  } else {
    lines = rows.map(r => ({ ...r, total: Object.values(r.periods||{}).reduce((s,v)=>s+v,0) }))
      .sort((a,b) => a.subCode.localeCompare(b.subCode))
    cols = ['Sub Code','Description','Source']
  }
  const grandTotal = lines.reduce((s,r)=>s+r.total,0)

  return (
    <div>
      <div style={{textAlign:'center',marginBottom:18,borderBottom:'2px solid #1C1A18',paddingBottom:12}}>
        <div style={{fontSize:18,fontWeight:800,color:'#1C1A18'}}>{project?.name || 'Project'}</div>
        <div style={{fontSize:14,fontWeight:600,color:'#0B3D91',marginTop:4}}>Total Actual Cost Summary {groupBy==='cost'?'— by Cost Code':'— by Sub Code'}</div>
        <div style={{fontSize:11,color:'#5A5650',marginTop:2}}>Cumulative up to <b>{monthLabel(lastPeriod)}</b></div>
      </div>
      <PrintSummaryBar summary={summary}/>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
        <thead>
          <tr style={{background:'#F5F1EA'}}>
            {cols.map(h=>(
              <th key={h} style={{textAlign:'left',padding:'8px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#5A5650',borderBottom:'2px solid #DDD9D0'}}>{h}</th>
            ))}
            <th style={{textAlign:'right',padding:'8px 10px',fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#0B3D91',borderBottom:'2px solid #DDD9D0'}}>Total Actual Cost</th>
          </tr>
        </thead>
        <tbody>
          {lines.filter(r=>r.total>0).map((r,i) => (
            <tr key={i} style={{borderBottom:'1px solid #F0EDE6'}}>
              {groupBy==='cost' ? (
                <>
                  <td style={{padding:'6px 10px',fontWeight:600}}>{r.costCode}</td>
                  <td style={{padding:'6px 10px',color:'#5A5650'}}>{r.costCodeDesc || '—'}</td>
                </>
              ) : (
                <>
                  <td style={{padding:'6px 10px',fontWeight:600}}>{r.subCode}</td>
                  <td style={{padding:'6px 10px',color:'#5A5650'}}>{r.costCodeDesc || '—'}</td>
                  <td style={{padding:'6px 10px',whiteSpace:'nowrap'}}>{r.source}</td>
                </>
              )}
              <td style={{padding:'6px 10px',textAlign:'right',fontWeight:700}}>{fmt(r.total)}</td>
            </tr>
          ))}
          {lines.filter(r=>r.total>0).length === 0 && (
            <tr><td colSpan={cols.length+1} style={{padding:20,textAlign:'center',color:'#9A948C'}}>No data</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid #1C1A18'}}>
            <td colSpan={cols.length} style={{padding:'8px 10px',fontWeight:700,textTransform:'uppercase',fontSize:11}}>Grand Total</td>
            <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontSize:12}}>{fmt(grandTotal)}</td>
          </tr>
        </tfoot>
      </table>
      <div style={{marginTop:24,fontSize:9.5,color:'#9A948C'}}>Printed on {new Date().toLocaleDateString('en-GB')}</div>
    </div>
  )
}
